import {
  put, takeEvery, all, select, call, fork, takeLatest,
} from 'redux-saga/effects';

import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import { exception } from 'circe/logger';
import moment from 'moment';
import { show as showNotification } from 'circe/notifications/actions';
import * as I18n from 'circe/I18n';
import * as listingSaga from 'circe/listing/saga';
import { getCurrency } from 'domain/fuse/preferences/currency';
import transactionsSaga from './transactions/saga';

import {
  fetch,
  fetchNext,
  fetchPrev,
  fetched,
  fetching,
  revoke,
  revoked,
  revokeFailed,
  replenish,
  replenished,
  replenishFailed,
  create,
  created,
  createFailed,
  invoiceCreate,
  invoiceCreated,
  invoiceCreateFailed,
  activate,
  activated,
  cache,
} from './actions';

import * as api from './api';

import {
  getResultById,
  getNextCursor,
  getHasNext,
  getHasPrev,
  getPrevCursor,
  getFromCache,
  getFilters,
} from './selectors';

function* doFetch({ payload }) {
  try {
    const { filters } = payload;
    const state = yield select();
    const DEFAULT_CURRENCY = yield call(getCurrency, state);

    yield fork(listingSaga.doFetch, {
      api: api.fetch,
      filters,
      fetched,
      cache,
      getFromCache,
      getCurrentAuthHeader,
      normalizer: (response) => response.results.map((item) => ({
        ...item,
        creditTerms: {
          ...item.creditTerms,
          currency: DEFAULT_CURRENCY,
        },
      })),
    });
  } catch (e) {
    yield put(
      showNotification(
        I18n.str('creditTermsFetchFailed', 'Unable to fetch credit terms.'),
        'error',
      ),
    );
    exception(e);
  }
}

export function* doFetchNext() {
  yield fork(listingSaga.doFetchNext, {
    fetching,
    getHasNext,
    getNextCursor,
    getFilters,
    doFetch,
  });
}

export function* doFetchPrev() {
  yield fork(listingSaga.doFetchPrev, {
    fetching,
    getHasPrev,
    getPrevCursor,
    getFilters,
    doFetch,
  });
}

function* doRevoke({ payload }) {
  const { id } = payload;

  try {
    const state = yield select();

    const creditTerm = yield call(getResultById, state, id);

    yield call(api.revoke, id);

    yield put(
      revoked(id, {
        creditTerms: {
          ...creditTerm.creditTerms,
          active: false,
        },
      }),
    );

    yield put(
      showNotification(
        I18n.str('revokeCreditTermsSuccess', 'Credit term has been revoked.'),
        'success',
      ),
    );
  } catch (e) {
    yield put(
      showNotification(
        I18n.str('revokeCreditTermsFailed', 'Unable to revoke credit term.'),
        'error',
      ),
    );
    yield put(revokeFailed(id));
    exception(e);
  }
}

function* doActivate({ payload }) {
  const { id } = payload;

  try {
    const state = yield select();

    const creditTerm = yield call(getResultById, state, id);

    const formData = {
      amountPerTransaction: creditTerm.creditTerms.amountPerTransaction,
      creditLimit: creditTerm.creditTerms.creditLimit,
      name: creditTerm.creditTerms.name,
      transactionPerDay: creditTerm.creditTerms.transactionPerDay,
      startDate: moment(creditTerm.creditTerms.startDate).toISOString(),
      expiryDate: moment(creditTerm.creditTerms.expiryDate).toISOString(),
    };

    yield call(api.replenish, id, {
      id,
      ownerId: creditTerm.ownerId,
      ...formData,
      active: true,
    });

    yield put(
      activated(id, {
        creditTerms: {
          ...creditTerm.creditTerms,
          active: true,
        },
      }),
    );

    yield put(
      showNotification(
        I18n.str('activateCreditTermsSuccess', 'Credit term has been activated.'),
        'success',
      ),
    );
  } catch (e) {
    yield put(
      showNotification(
        I18n.str('activateCreditTermsFailed', 'Unable to activate credit term.'),
        'error',
      ),
    );
    yield put(activateFailed(id));
    exception(e);
  }
}

function* doReplenish({ payload }) {
  const { id, data } = payload;

  try {
    const state = yield select();

    const creditTerm = getResultById(state, id);

    const formData = {
      amountPerTransaction: data.amountPerTransaction,
      creditLimit: data.creditLimit,
      name: data.name,
      transactionPerDay: data.transactionPerDay,
      startDate: moment(data.startDate).toISOString(),
      expiryDate: moment(data.expiryDate).toISOString(),
    };

    yield call(api.replenish, id, {
      id,
      ownerId: creditTerm.ownerId,
      ...formData,
    });

    yield put(
      replenished(id, {
        creditTerms: { ...creditTerm.creditTerms, ...formData },
        usage: 0,
      }),
    );
    yield put(
      showNotification(
        I18n.str('updateCreditTermsSuccess', 'Credit term has been updated successfully.'),
        'success',
      ),
    );
  } catch (e) {
    if (e.code.status === 400) {
      yield put(showNotification(e.code.message, 'error'));
    } else {
      yield put(
        showNotification(
          I18n.str('replenishCreditTermsFailed', 'Unable to update credit term.'),
          'error',
        ),
      );
    }

    yield put(replenishFailed(id));
    exception(e);
  }
}

function* doCreate({ payload }) {
  const { data } = payload;

  try {
    const resp = yield call(api.create, {
      ...data,
      startDate: moment(data.startDate).toISOString(),
      expiryDate: moment(data.expiryDate).toISOString(),
    });

    yield put(
      showNotification(
        I18n.str('createCreditTermSuccess', 'New credit term has been created.'),
        'success',
      ),
    );

    yield put(created(resp));
  } catch (e) {
    yield put(
      showNotification(
        I18n.str('createCreditTermFailed', 'Unable to create credit term.'),
        'error',
      ),
    );
    yield put(createFailed());
    exception(e);
  }
}

function* doInvoiceCreate({ payload }) {
  const { id, data } = payload;

  try {
    yield call(api.invoiceCreate, id);

    yield put(
      showNotification(
        I18n.str('creditTermInvoiceCreateSuccess', 'Credit term Invoice has been created.'),
        'success',
      ),
    );

    yield put(invoiceCreated(id));
  } catch (e) {
    yield put(
      showNotification(
        I18n.str('creditTermInvoiceCreateFailed', 'Unable to create credit term invoice.'),
        'error',
      ),
    );
    yield put(invoiceCreateFailed(id));
    exception(e);
  }
}

function* watcher(context) {
  yield all([
    takeLatest(fetch().type, doFetch),
    takeLatest(fetchNext().type, doFetchNext),
    takeLatest(fetchPrev().type, doFetchPrev),
    takeEvery(create().type, doCreate),
    takeEvery(activate().type, doActivate),
    takeEvery(revoke().type, doRevoke),
    takeEvery(replenish().type, doReplenish),
    takeEvery(invoiceCreate().type, doInvoiceCreate),
    transactionsSaga(),
  ]);
}

export default watcher;
